import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { DamEcoModeVersion } from '~/providers/types/damEcoMode';
import { colors } from '~/styles/colors';
import { ECO_MODE_LABEL_KEYS } from '../constants';
import { DamEcoModePendingChanges, getSwitchBlockedMessageKey } from '../ecoModeView';
import { mobileQuery, PrimaryButton, SecondaryButton } from '../ds';
import { SwitcherBar, SwitcherHint, SwitcherInfo } from '../styles';
import { ModalShell } from './schedules/ModalShell';
import { ModalSubtitle, ModalTitle } from './schedules/styles';

const ConfirmBody = styled.div`
  padding: 28px 32px 30px;
  font-family: Inter, sans-serif;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 28px;
`;

const SwitchButton = styled(SecondaryButton)`
  border-color: ${colors.brand.principal};
  color: ${colors.brand.principal};
  white-space: nowrap;

  ${mobileQuery} {
    width: 100%;
    white-space: normal;
  }
`;

interface EcoModeSwitcherProps {
  activeMode: DamEcoModeVersion;
  /** Alterações pendentes em cada tela; qualquer uma bloqueia a alternância. */
  pendingChanges: DamEcoModePendingChanges;
  isSwitching: boolean;
  onConfirm: (mode: DamEcoModeVersion) => void;
}

/** Indicação do modo ativo e alternância, com confirmação; bloqueada enquanto houver alterações não salvas. */
export function EcoModeSwitcher({
  activeMode,
  pendingChanges,
  isSwitching,
  onConfirm,
}: Readonly<EcoModeSwitcherProps>): ReactElement {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const blockedMessageKey = getSwitchBlockedMessageKey(pendingChanges);
  const targetMode: DamEcoModeVersion = activeMode === 'LEGACY' ? 'SIMPLIFIED' : 'LEGACY';
  const activeLabel = t(ECO_MODE_LABEL_KEYS[activeMode]);
  const targetLabel = t(ECO_MODE_LABEL_KEYS[targetMode]);

  function onConfirmSwitch(): void {
    setIsConfirmOpen(false);
    onConfirm(targetMode);
  }

  return (
    <>
      <SwitcherBar>
        <SwitcherInfo>
          <span data-testid="eco-modo-ativo">
            {t('ecoSimplificadoModoAtivoAtual')}
            {': '}
            <strong>{activeLabel}</strong>
          </span>
          {blockedMessageKey ? <SwitcherHint>{t(blockedMessageKey)}</SwitcherHint> : null}
        </SwitcherInfo>
        <SwitchButton
          type="button"
          disabled={!!blockedMessageKey || isSwitching}
          onClick={() => setIsConfirmOpen(true)}
          data-testid="eco-modo-alternar"
        >
          {t('ecoSimplificadoAlternarPara', { modo: targetLabel })}
        </SwitchButton>
      </SwitcherBar>
      {isConfirmOpen ? (
        <ModalShell width={480} onClose={() => setIsConfirmOpen(false)}>
          <ConfirmBody>
            <ModalTitle>{t('ecoSimplificadoConfirmarAlternanciaTitulo')}</ModalTitle>
            <ModalSubtitle>{t('ecoSimplificadoConfirmarAlternanciaTexto', { modo: targetLabel })}</ModalSubtitle>
            <ConfirmActions>
              <SecondaryButton type="button" onClick={() => setIsConfirmOpen(false)} data-testid="eco-modo-alternar-cancelar">
                {t('cancelar')}
              </SecondaryButton>
              <PrimaryButton type="button" onClick={onConfirmSwitch} data-testid="eco-modo-alternar-confirmar">
                {t('confirmar')}
              </PrimaryButton>
            </ConfirmActions>
          </ConfirmBody>
        </ModalShell>
      ) : null}
    </>
  );
}
